import * as React from 'react';
import { useEffect } from "react";
import { Chart } from 'chart.js';
import { SiteVariablesPrepared } from "@fluentui/react-northstar";
import { IChartData } from "../chart-types";
import { axesConfig, chartConfig, setTooltipColorScheme, hexToRgb } from "../chart-utils";
import { ChartContainer } from "./chart-container";
import { getText } from "../../../translations";

const LineChartNoAxes = ({
    title,
    data,
    siteVariables,
    gradients,
    customWidth,
    customHeight,
    theme,
}: {
    title: string;
    data: IChartData;
    siteVariables: SiteVariablesPrepared;
    gradients?: boolean;
    customWidth?: any;
    customHeight?: any;
    theme?: string;
}) => {
    const { colorScheme, t } = siteVariables;
    const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
    const chartRef = React.useRef<Chart | undefined>();
    const chartId = React.useMemo(
        () => Math.random().toString(36).substr(2, 9),
        []
    );
    const chartDataPointColors = React.useMemo(
        () => [
            '#27438C',
            '#D8D8D8',
        ],
        [theme]
    );

    const chartDataPointColorsDark = React.useMemo(
        () => [
            '#508BD5',
            '#D8D8D8',
        ],
        [theme]
    );

    const determineTrendColor = (data: number[] | any, upColor: string, downColor: string): string => {
        if (data.length < 2) {
            return upColor;
        }

        let increasingCount = 0;
        let decreasingCount = 0;

        for (let i = 1; i < data.length; i++) {
            if (data[i] > data[i - 1]) {
                increasingCount++;
            } else if (data[i] < data[i - 1]) {
                decreasingCount++;
            }
        }

        if (decreasingCount > increasingCount) {
            return downColor;
        }

        return data[data.length - 1] < data[0] ? downColor : upColor;
    };

    const createAreaChartDataPoints = (
        ctx: CanvasRenderingContext2D
    ): Chart.ChartDataSets[] =>
        Array.from(data.datasets, (set, i) => {
            const trendColor = determineTrendColor(set.data, theme === "dark" ? chartDataPointColorsDark[i] : chartDataPointColors[i], "#ED1B23");

            chartDataPointColors[i] = trendColor;
            chartDataPointColorsDark[i] = trendColor;

            const gradientStroke = ctx.createLinearGradient(0, 0, 0, ctx.canvas.clientHeight * 0.9);
            const hoverGradientStroke = ctx.createLinearGradient(0, 0, 0, ctx.canvas.clientHeight * 0.9);

            if (theme === "dark") {
                const colorRGB = hexToRgb(chartDataPointColorsDark[i]);
                gradientStroke.addColorStop(0, `rgba(${colorRGB}, .4)`);
                gradientStroke.addColorStop(1, `rgba(${colorRGB}, .0)`);
                hoverGradientStroke.addColorStop(0, `rgba(${colorRGB}, .6)`);
                hoverGradientStroke.addColorStop(1, `rgba(${colorRGB}, .0)`);
            } else {
                const colorRGB = hexToRgb(chartDataPointColors[i]);
                gradientStroke.addColorStop(0, `rgba(${colorRGB}, .4)`);
                gradientStroke.addColorStop(1, `rgba(${colorRGB}, .0)`);
                hoverGradientStroke.addColorStop(0, `rgba(${colorRGB}, .6)`);
                hoverGradientStroke.addColorStop(1, `rgba(${colorRGB}, .0)`);
            }

            let length = set.data.length ? set.data.length - 1 : 0;

            let dataPointConfig = {
                label: getText(t?.locale, set.label),
                data: set.data,
                borderColor: chartDataPointColors[i],
                hoverBorderColor: chartDataPointColors[i],
                hoverBorderWidth: 1,
                backgroundColor: gradientStroke as any,
                hoverBackgroundColor: hoverGradientStroke as any,
                borderWidth: 1,
                pointBorderColor: chartDataPointColors[i],
                pointBackgroundColor: chartDataPointColors[i],
                pointHoverBackgroundColor: chartDataPointColors[i],
                pointHoverBorderColor: chartDataPointColors[i],
                pointHoverBorderWidth: 0,
                borderCapStyle: "round",
                borderJoinStyle: "round",
                pointBorderWidth: 0,
                pointRadius: new Array(length).fill(0).concat(2),
                pointHoverRadius: 2,
                pointStyle: "circle",
                borderDash: [],
            };

            if (theme === "dark") {
                dataPointConfig = {
                    ...dataPointConfig,
                    borderColor: chartDataPointColorsDark[i],
                    hoverBorderColor: chartDataPointColorsDark[i],
                    hoverBorderWidth: 1,
                    backgroundColor: gradientStroke as any,
                    hoverBackgroundColor: hoverGradientStroke as any,
                    borderWidth: 1,
                    pointBorderColor: chartDataPointColorsDark[i],
                    pointBackgroundColor: chartDataPointColorsDark[i],
                    pointHoverBackgroundColor: chartDataPointColorsDark[i],
                    pointHoverBorderColor: chartDataPointColorsDark[i],
                    pointHoverBorderWidth: 0,
                    borderCapStyle: "round",
                    borderJoinStyle: "round",
                    pointBorderWidth: 0,
                    pointRadius: new Array(length).fill(0).concat(3),
                    pointHoverRadius: 2,
                    pointStyle: "circle",
                    borderDash: [],
                } as any;
            }

            return dataPointConfig as Chart.ChartDataSets;
        });

    useEffect(() => {
        if (!canvasRef.current) return;
        const ctx = canvasRef.current.getContext("2d");
        if (!ctx) return;

        const config: any = chartConfig({ type: "line" });

        config.options.tooltips.intersect = false;

        chartRef.current = new Chart(ctx, {
            ...(config as any),
            data: {
                labels: Array.isArray(data.labels)
                    ? data.labels.map((label) => getText(t?.locale, label))
                    : getText(t?.locale, data.labels),
                datasets: data.datasets,
            },
            options: {
                responsive: true,
                legend: {
                    display: false,
                },
                layout: {
                    padding: {
                        left: 4,
                        right: 8,
                        top: 8,
                        bottom: 2,
                    },
                },
                tooltips: {
                    caretPadding: 2,
                    caretSize: 4,
                    yPadding: 2,
                    xPadding: 20,
                    callbacks: {
                        title: (tooltipItems: any) => {
                        },
                    },
                },
                scales: {
                    xAxes: [{
                        display: false,
                        ticks: {
                            display: false
                        },
                    }],
                    yAxes: [{
                        display: false,
                        ticks: {
                            beginAtZero: true
                        }
                    }],
                }
            },
        });

        return () => {
            if (!chartRef.current) return;
            chartRef.current.destroy();
        };
    }, [theme, data]);

    /**
     * Theme updates
     */
    useEffect(() => {
        if (!chartRef.current) return;
        if (!canvasRef.current) return;
        const ctx = canvasRef.current.getContext("2d");
        if (!ctx) return;
        // Apply new colors scheme for data points
        chartRef.current.data.datasets = createAreaChartDataPoints(ctx)

        // Update tooltip colors scheme
        setTooltipColorScheme({
            chart: chartRef.current,
            siteVariables,
            chartDataPointColors,
        });

        // Update axeses
        axesConfig({ chart: chartRef.current, ctx, colorScheme });
        // Show style changes
        chartRef.current.update();
    }, [theme, data]);

    function onLegendClick(datasetIndex: number) {
        if (!chartRef.current) return;
        chartRef.current.data.datasets![datasetIndex].hidden = !chartRef.current
            .data.datasets![datasetIndex].hidden;
        chartRef.current.update();
    }

    return (
        <ChartContainer
            siteVariables={siteVariables}
            data={data}
            chartDataPointColors={chartDataPointColors}
            onLegendClick={onLegendClick}
            hideLegend={true}
            customWidth={customWidth ? customWidth : "100%"}
        >
            <canvas
                id={chartId}
                ref={canvasRef}
                tabIndex={0}
                style={{
                    userSelect: "none",
                }}
                aria-label={title}
            >
                {data.datasets.map((set, setKey) =>
                    (set.data as number[]).forEach((item: number, itemKey: number) => (
                        // Generated tooltips for screen readers
                        <div key={itemKey} id={`${chartId}-tooltip-${setKey}-${itemKey}`}>
                            <p>{item}</p>
                            <span>
                                {getText(t?.locale, set.label)}: {set.data[itemKey]}
                            </span>
                        </div>
                    ))
                )}
            </canvas>
        </ChartContainer>
    );
};

export const numberFormat = (value: number | string): string =>
    String(value)
        .split("")
        .reverse()
        .join("")
        .replace(/(\d{3})/g, "$1.")
        .replace(/\,$/, "")
        .split("")
        .reverse()
        .join("");

export default LineChartNoAxes;